<main class="landing">
  <app-hero />

  <!-- Social proof row -->
  <section class="social-proof" aria-label="{{ appName }} by the numbers">
    <div class="proof-item">
      <strong>100%</strong>
      <span>Anonymous</span>
    </div>
    <div class="proof-item">
      <strong>0</strong>
      <span>Accounts needed</span>
    </div>
    <div class="proof-item">
      <strong>190+</strong>
      <span>Countries</span>
    </div>
    <div class="proof-item">
      <strong>24/7</strong>
      <span>Always online</span>
    </div>
  </section>

  <section class="stats" aria-label="How {{ appName }} works">
    <div><strong>01</strong><span>Choose your interests</span></div>
    <div><strong>02</strong><span>Meet someone new</span></div>
    <div><strong>03</strong><span>Talk at your pace</span></div>
    <div><strong>04</strong><span>Leave when you want</span></div>
  </section>

  <section class="home-blog" aria-labelledby="home-blog-title">
    <div class="home-section-heading">
      <div>
        <p class="eyebrow">{{ copy().blogEyebrow }}</p>
        <h2 id="home-blog-title">{{ copy().blogTitle }}</h2>
      </div>
      <a class="text-link" routerLink="/blog">{{ copy().viewPosts }} </a>
    </div>
    <div class="home-blog-grid">
      @for (article of blogArticles; track article.slug) {
        <a
          class="home-blog-card"
          [class]="'home-blog-card ' + article.accent"
          [routerLink]="'/blog/' + article.slug"
        >
          <span>{{ article.category }}</span>
          <strong>{{ article.title }}</strong>
          <small>{{ appName }} editorial team · {{ article.date }}</small>
        </a>
      }
    </div>
  </section>

  <section class="feature-grid">
    <div class="section-heading">
      <div class="eyebrow">WHY {{ appName | uppercase }}</div>
      <h2>{{ copy().whyTitle }}</h2>
    </div>
    <div class="feature-card dark">
      <span class="icon-badge">◌</span>
      <h3>{{ copy().randomChat }}</h3>
      <p>{{ copy().randomChatText }}</p>
    </div>
    <div class="feature-card">
      <span class="icon-badge lilac">✦</span>
      <h3>{{ copy().interests }}</h3>
      <p>{{ copy().interestsText }}</p>
    </div>
    <div class="feature-card">
      <span class="icon-badge green">⌁</span>
      <h3>{{ copy().privacy }}</h3>
      <p>{{ copy().privacyText }}</p>
    </div>
    <div class="feature-card">
      <span class="icon-badge peach">⌘</span>
      <h3>{{ copy().control }}</h3>
      <p>{{ copy().controlText }}</p>
    </div>
  </section>

  <section class="safety-banner">
    <div>
      <div class="eyebrow">{{ copy().safetyEyebrow }}</div>
      <h2>{{ copy().safetyTitle }}</h2>
      <p>{{ copy().safetyText }}</p>
    </div>
    <a class="secondary-button" routerLink="/safety-tips">{{
      copy().safetyLink
    }}</a>
  </section>

  <section class="seo-content" aria-labelledby="seo-title">
    <div class="seo-copy">
      <p class="eyebrow">MEET SOMEONE NEW</p>
      <h2 id="seo-title">{{ copy().seoTitle }}</h2>
      <p>
        {{ appName }} is a free anonymous text chat website for respectful
        conversations without an account. We are building for people who want a
        genuine moment of connection, not another public feed.
      </p>
      <div class="seo-grid">
        <article>
          <h3>Talk to random strangers online</h3>
          <p>
            Start a friendly random chat with someone new. Every conversation is
            private, anonymous, and ends when you choose.
          </p>
        </article>
        <article>
          <h3>Make international friends</h3>
          <p>
            Meet people worldwide and start talking with someone who enjoys the
            same topics. Interest matching makes the first hello easier.
          </p>
        </article>
        <article>
          <h3>A simple Omegle alternative</h3>
          <p>
            Looking for an Omegle alternative? {{ appName }} offers
            interest-based anonymous text chat with a calm, guest-first
            experience and no camera required.
          </p>
        </article>
      </div>
    </div>
    <div class="faq-list" itemscope itemtype="https://schema.org/FAQPage">
      <h2>{{ copy().faqTitle }}</h2>
      <details
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <summary itemprop="name">
          Can I chat with strangers without signing up?
        </summary>
        <p
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <span itemprop="text"
            >Yes. {{ appName }} lets guests start an anonymous random chat
            without creating an account. Just pick a nickname and you're ready
            to go.</span
          >
        </p>
      </details>
      <details
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <summary itemprop="name">
          Is {{ appName }} a free Omegle alternative?
        </summary>
        <p
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <span itemprop="text"
            >Yes. {{ appName }} is a free anonymous text chat platform. It works
            as a friendly Omegle alternative with interest-based matching and no
            camera required.</span
          >
        </p>
      </details>
      <details
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <summary itemprop="name">
          Can I make friends from other countries?
        </summary>
        <p
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <span itemprop="text"
            >Yes. {{ appName }} connects you with people around the world
            through shared interests.</span
          >
        </p>
      </details>
      <details
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <summary itemprop="name">
          Is {{ appName }} a video chat website?
        </summary>
        <p
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <span itemprop="text"
            >Yes. {{ appName }} now offers both anonymous text chat and live
            video chat with strangers. Text chat requires no camera; video chat
            lets you connect face-to-face — your choice.</span
          >
        </p>
      </details>
      <details
        itemprop="mainEntity"
        itemscope
        itemtype="https://schema.org/Question"
      >
        <summary itemprop="name">
          How does interest-based matching work?
        </summary>
        <p
          itemprop="acceptedAnswer"
          itemscope
          itemtype="https://schema.org/Answer"
        >
          <span itemprop="text"
            >Add topics you enjoy — music, travel, gaming, coding, and more.
            {{ appName }} prioritises matching you with people who share those
            interests, so every conversation already has something in common
            from the start.</span
          >
        </p>
      </details>
    </div>
  </section>
</main>
